import { commandCenterPath } from './commandCenterRoutes'
export const C = {
  CYAN: "#0891b2",
  BLUE: "#2563eb",
  GREEN: "#059669",
  RED: "#dc2626",
  PURPLE: "#7c3aed",
  AMBER: "#f59e0b",
  PINK: "#ec4899",
  TEAL: "#14b8a6",
  ORANGE: "#f97316",
  LIME: "#84cc16",
  INDIGO: "#6366f1",
  ROSE: "#f43f5e",
} as const;

export type BuilderStatus = "active" | "idle";

export interface DivisionBuilder {
  sysName: string;
  role: string;
  status: BuilderStatus;
  color: string;
  suggestion: string;
}

export interface DivisionReport {
  title: string;
  summary: string;
  items: string[];
}

export interface DivisionDefinition {
  id: string;
  name: string;
  color: string;
  desc: string;
  builders: DivisionBuilder[];
  divisionReport: DivisionReport;
  placeholder: string;
  sections: string[];
}

export interface NotificationItem {
  from: string;
  subject: string;
  time: string;
  priority: "high" | "medium" | "low";
}

export interface NotificationCategory {
  id: string;
  label: string;
  icon: string;
  color: string;
  count: number;
  urgent: number;
  items: NotificationItem[];
}

// PHASE_2M — the home "signals" row. Honest ROUTING cards (no fabricated
// counts/urgency): each card opens a real V2 surface. "Scheduled" became
// Notifications (/notifications). Icons are SVG components resolved by id in
// SquareCodeCommandCenter (no emoji).
export interface HomeSignalCard {
  id: "divisions" | "projects" | "notifications" | "calendar";
  label: string;
  desc: string;
  color: string;
  to: string;
}

export const HOME_SIGNAL_CARDS: HomeSignalCard[] = [
  { id: "divisions", label: "Divisions", desc: "Open a division workspace", color: C.CYAN, to: commandCenterPath('workspaces/all') },
  { id: "projects", label: "Projects", desc: "Open the project portfolio", color: C.PURPLE, to: commandCenterPath('projects') },
  { id: "notifications", label: "Notifications", desc: "What needs attention now", color: C.AMBER, to: commandCenterPath('notifications') },
  { id: "calendar", label: "Calendar", desc: "Open the Quam Calendar", color: C.GREEN, to: commandCenterPath('calendar') },
];

export interface WorkspaceReport {
  agent: string;
  sysName?: string;
  color: string;
  type: "system" | "builder";
  text: string;
}

export interface ChatMessage {
  who: string;
  color: string;
  text: string;
}

export const DIVISIONS: DivisionDefinition[] = [
  {
    id: "strategy",
    name: "Strategy",
    color: C.CYAN,
    desc: "Market analysis, competitive intelligence",
    builders: [
      {
        sysName: "ATHENA",
        role: "Lead Strategist",
        status: "active",
        color: C.CYAN,
        suggestion:
          "Compass Indianapolis expansion confirmed for April. Recommend pre-emptive agent recruitment push within 2 weeks to secure first-mover positioning in the transplant buyer segment.",
      },
      {
        sysName: "VULCAN",
        role: "Scenario Planner",
        status: "active",
        color: C.TEAL,
        suggestion:
          "3 scenario models ready for Q2: aggressive at 15 agents in 60 days, moderate at 10 in 90, and conservative at 7 in 120. Aggressive recommended given the shrinking market window.",
      },
      {
        sysName: "PROMETHEUS",
        role: "Trend Forecaster",
        status: "idle",
        color: C.BLUE,
        suggestion:
          "Remote work migration accelerating. 34% of Indianapolis buyers are out-of-state relocations. The Chicago corridor is strongest at 22% of all inbound traffic.",
      },
      {
        sysName: "ORACLE_S",
        role: "Intelligence Analyst",
        status: "active",
        color: C.INDIGO,
        suggestion:
          "Competitor analysis complete across 14 brokerages in the Indianapolis metro. Three are expanding, two are contracting. Silver Key's tech-first approach has zero direct competitors in the transplant buyer vertical: a 6-month advantage window before Compass can replicate.",
      },
    ],
    divisionReport: {
      title: "Q2 Strategic Priorities",
      summary: "Market window narrowing. Compass 3-4 weeks out.",
      items: ["Response Plan", "LOI: 11/15", "Entry: 30d", "Budget: Pending"],
    },
    placeholder: "Analyze competitive landscape...",
    sections: ["Summary", "Positioning", "Competitors", "Recommendations"],
  },
  {
    id: "operations",
    name: "Operations",
    color: C.BLUE,
    desc: "Logistics, workflow, automation",
    builders: [
      {
        sysName: "APOLLO",
        role: "Operations Lead",
        status: "active",
        color: C.BLUE,
        suggestion:
          "Onboarding pipeline has a 3-day bottleneck at the background check stage. Recommend parallel processing to start training while checks complete, saving 72 hours per cohort.",
      },
      {
        sysName: "HERMES",
        role: "Workflow Architect",
        status: "active",
        color: C.TEAL,
        suggestion:
          "Seven manual handoff points identified across the pipeline. Automation candidates include document routing, CRM sync, and commission calculations for an estimated 40% time savings.",
      },
      {
        sysName: "FORGE",
        role: "Process Engineer",
        status: "idle",
        color: C.INDIGO,
        suggestion:
          "Standardized onboarding template now covers 14 steps with 5 fully automated. Average time to agent activation is 4.2 days.",
      },
    ],
    divisionReport: {
      title: "Ops Health",
      summary: "Pipeline 82%. 3 automations live.",
      items: ["Pipeline: 82%", "Auto: 12/18", "Onboard: 4.2d", "Next: Comm"],
    },
    placeholder: "Design pipeline...",
    sections: ["Pipeline", "Recruitment", "Training", "Deploy"],
  },
  {
    id: "finance",
    name: "Finance",
    color: C.GREEN,
    desc: "Financial modeling, unit economics",
    builders: [
      {
        sysName: "MORPHEUS",
        role: "Financial Architect",
        status: "active",
        color: C.AMBER,
        suggestion:
          "Customer acquisition cost dropped from $2,100 to $1,800. Lifetime value to CAC ratio now sits at 2.3x and is tracking toward 3x by Q3. These are investor-ready metrics.",
      },
      {
        sysName: "ORACLE",
        role: "Data Analyst",
        status: "active",
        color: C.GREEN,
        suggestion:
          "February closed with 14 transactions versus 12 projected. March pipeline shows 16 probable closings. Breakeven timeline has moved up by 6 weeks.",
      },
      {
        sysName: "MIDAS",
        role: "Revenue Strategist",
        status: "idle",
        color: C.LIME,
        suggestion:
          "The referral program is generating 18% of all leads at zero acquisition cost. Doubling the incentive to $1,000 would yield an estimated 2.4x return on investment.",
      },
    ],
    divisionReport: {
      title: "Revenue March",
      summary: "12% above forecast. 78% margin.",
      items: ["Rev: $58.8K", "Margin: 78%", "CAC: $1.8K", "BE: Q3"],
    },
    placeholder: "Build revenue model...",
    sections: ["Economics", "Projections", "Sensitivity", "Breakeven"],
  },
  {
    id: "advisory",
    name: "Advisory",
    color: C.INDIGO,
    desc: "Legal, compliance, policy",
    builders: [
      {
        sysName: "JURIS",
        role: "Legal Counsel",
        status: "idle",
        color: C.INDIGO,
        suggestion:
          "Meridian partnership agreement has two clauses requiring revision: the non-compete scope needs narrowing and the IP assignment clause needs more specificity.",
      },
      {
        sysName: "SENTINEL",
        role: "Compliance Officer",
        status: "idle",
        color: C.PURPLE,
        suggestion:
          "Quarterly compliance audit is due March 15. All OLIVIUM pillars are currently green. FTC guidelines are up to date.",
      },
      {
        sysName: "ADVOCATE",
        role: "Policy Writer",
        status: "idle",
        color: C.BLUE,
        suggestion:
          "Agent independent contractor agreements require a commission structure update. Draft with three changes is ready for review.",
      },
    ],
    divisionReport: {
      title: "Legal Status",
      summary: "Compliance current. No litigation.",
      items: ["Status: GREEN", "Reviews: 2", "Agreements: 1", "Risk: None"],
    },
    placeholder: "Review compliance...",
    sections: ["Regulatory", "Risk", "Compliance", "Actions"],
  },
  {
    id: "comms",
    name: "Comms",
    color: C.PURPLE,
    desc: "Messaging, investor comms, PR",
    builders: [
      {
        sysName: "ECHO",
        role: "Comms Lead",
        status: "active",
        color: C.BLUE,
        suggestion:
          "Investor update has been drafted with an urgency framing approach. Early testing shows a 34% higher open rate compared to the standard quarterly format.",
      },
      {
        sysName: "HERALD",
        role: "PR Strategist",
        status: "active",
        color: C.PURPLE,
        suggestion:
          "Indianapolis Business Journal is interested in a Silver Key feature story. The pitch angle is tech-first brokerage targeting transplant buyers.",
      },
      {
        sysName: "LYRIC",
        role: "Brand Writer",
        status: "idle",
        color: C.PINK,
        suggestion:
          "A/B testing results show 'Own your pipeline' outperformed 'Join the future' by 28% across all audience segments.",
      },
    ],
    divisionReport: {
      title: "Comms Pipeline",
      summary: "3 investor comms. PR active.",
      items: ["Updates: 3", "PR: 1", "A/B: +28%", "Brand: v2.1"],
    },
    placeholder: "Draft update...",
    sections: ["Highlights", "Metrics", "Product", "Outlook"],
  },
  {
    id: "capital",
    name: "Capital",
    color: C.ORANGE,
    desc: "Budgets, cash flow, fundraising",
    builders: [
      {
        sysName: "ATLAS",
        role: "Budget Controller",
        status: "idle",
        color: C.ORANGE,
        suggestion:
          "Q2 budget stands at $625K with a monthly burn rate of $208K. Current runway is 9.2 months. Azure credits can extend that to 12.8 months.",
      },
      {
        sysName: "FORTUNE",
        role: "Fundraise Lead",
        status: "idle",
        color: C.GREEN,
        suggestion:
          "Series A targeting $2.5M at a $12M valuation. Four venture capital firms and two angel investors are currently in active discussion.",
      },
      {
        sysName: "LEDGER",
        role: "Financial Analyst",
        status: "idle",
        color: C.AMBER,
        suggestion:
          "Marketing budget is 8% over allocation but generating 22% more qualified leads. Recommend shifting trade show budget to digital channels.",
      },
    ],
    divisionReport: {
      title: "Finance Overview",
      summary: "Cash healthy. Azure $150K approved.",
      items: ["Runway: 9.2mo", "Azure: $150K", "Series A: 6", "Burn: $208K"],
    },
    placeholder: "Create budget...",
    sections: ["Budget", "Burn", "Runway", "Fundraise"],
  },
  {
    id: "creative",
    name: "Creative",
    color: C.PINK,
    desc: "Design, brand, pitch styling",
    builders: [
      {
        sysName: "AURA",
        role: "Visual Director",
        status: "active",
        color: C.PINK,
        suggestion:
          "Investor deck has been finalized with the dark theme and Silver Key accent colors. Six slides with four embedded charts.",
      },
      {
        sysName: "PRISM",
        role: "Brand Designer",
        status: "idle",
        color: C.PURPLE,
        suggestion:
          "Brand audit identified 3 outdated logos across public materials. Updated assets have been pushed to the CDN.",
      },
      {
        sysName: "CANVAS",
        role: "Layout Specialist",
        status: "idle",
        color: C.ROSE,
        suggestion:
          "The redesigned one-pager shows a 40% improvement in scan-time. Users now spend 12 seconds on value propositions.",
      },
    ],
    divisionReport: {
      title: "Creative Output",
      summary: "Deck complete. Pager +40%.",
      items: ["Deck: OK", "Audit: 3", "Pager: +40%", "Next: Kit"],
    },
    placeholder: "Design deck...",
    sections: ["Cover", "Colors", "Type", "Layouts"],
  },
  {
    id: "security",
    name: "Olivium",
    color: C.RED,
    desc: "OLIVIUM, threats, encryption",
    builders: [
      {
        sysName: "OBSIDIAN",
        role: "Security Lead",
        status: "active",
        color: C.RED,
        suggestion:
          "All seven OLIVIUM security pillars are fully operational with zero vulnerabilities detected in the latest scan.",
      },
      {
        sysName: "CIPHER",
        role: "Encryption Spec",
        status: "idle",
        color: C.ROSE,
        suggestion:
          "Post-quantum cryptography migration is at 68% completion. NIST standards have been applied to all personally identifiable information.",
      },
      {
        sysName: "WARDEN",
        role: "Access Controller",
        status: "idle",
        color: C.ORANGE,
        suggestion:
          "RBAC audit flagged 2 accounts with elevated privileges that exceed their role requirements.",
      },
    ],
    divisionReport: {
      title: "OLIVIUM Status",
      summary: "All pillars green. PQ 68%.",
      items: ["OLIVIUM: OK", "PQ: 68%", "Flags: 2", "Pen: Due"],
    },
    placeholder: "Run audit...",
    sections: ["Threats", "Compliance", "Vulns", "Fix"],
  },
  {
    id: "engineering",
    name: "Engineering",
    color: C.TEAL,
    desc: "Platform, infrastructure, APIs",
    builders: [
      {
        sysName: "STACK",
        role: "Full Stack Dev",
        status: "active",
        color: C.TEAL,
        suggestion:
          "Multi-artifact pipeline has been deployed to staging. Four concurrent builders tested with an average generation time of 14 seconds. WebSocket integration is complete and stable.",
      },
      {
        sysName: "VAULT",
        role: "Database Manager",
        status: "active",
        color: C.GREEN,
        suggestion:
          "Supabase is running 450+ tables with query performance at the 99th percentile under 120ms. Recommend partitioning the audit_logs table: growing at 2.3GB per month.",
      },
      {
        sysName: "BLUEPRINT",
        role: "Systems Architect",
        status: "active",
        color: C.CYAN,
        suggestion:
          "Service Bus architecture finalized with 8 topics, 12 subscriptions, and active dead-letter queue monitoring. Production-ready pending approval.",
      },
      {
        sysName: "BENCHMARK",
        role: "Performance Lead",
        status: "idle",
        color: C.AMBER,
        suggestion:
          "Load testing complete. System handles 200 concurrent users at sub-200ms response. Bottleneck at Azure OpenAI rate limits.",
      },
    ],
    divisionReport: {
      title: "Sprint - Week 12",
      summary: "Pipeline staging. DB p99 120ms.",
      items: ["Pipeline: OK", "DB: 120ms", "Bus: 8", "Load: 200"],
    },
    placeholder: "Design Service Bus...",
    sections: ["Topics", "Schema", "Fan-Out", "Errors"],
  },
  {
    id: "wellness",
    name: "Vault",
    color: C.LIME,
    desc: "Wellbeing, workload, productivity",
    builders: [
      {
        sysName: "HARMONY",
        role: "Wellbeing Monitor",
        status: "active",
        color: C.LIME,
        suggestion:
          "Current workload index is 7.2 out of 10, within healthy range. Engineering team elevated with 3 members logging over 52 hours. Sprint reduction recommended.",
      },
      {
        sysName: "SAGE",
        role: "Productivity Analyst",
        status: "idle",
        color: C.GREEN,
        suggestion:
          "Peak productivity window is 9 to 11 AM. Scheduling meetings during this window reduces output by 23%. Recommend meeting-free mornings.",
      },
      {
        sysName: "PULSE",
        role: "Activity Tracker",
        status: "idle",
        color: C.TEAL,
        suggestion:
          "Average Slack response time during business hours is 18 minutes. After-hours averages exceed 4 hours: healthy boundaries maintained.",
      },
    ],
    divisionReport: {
      title: "Wellness Weekly",
      summary: "7.2/10 healthy. Eng flagged.",
      items: ["Load: 7.2", "Eng: Flag", "AM: -23%", "Bounds: OK"],
    },
    placeholder: "Analyze workload...",
    sections: ["Distribution", "Burnout", "Patterns", "Actions"],
  },
];

export const NOTIF_CATS: NotificationCategory[] = [
  {
    id: "division",
    label: "Division",
    icon: "◆",
    color: C.CYAN,
    count: 6,
    urgent: 2,
    items: [
      { from: "Strategy", subject: "Compass confirmed", time: "2:15 PM", priority: "high" },
      { from: "Security", subject: "OLIVIUM green", time: "1:00 PM", priority: "high" },
    ],
  },
  {
    id: "project",
    label: "Project",
    icon: "◈",
    color: C.PURPLE,
    count: 4,
    urgent: 1,
    items: [{ from: "Silver Key", subject: "Deck ready", time: "2:30 PM", priority: "high" }],
  },
  {
    id: "scheduled",
    label: "Scheduled",
    icon: "◉",
    color: C.AMBER,
    count: 5,
    urgent: 1,
    items: [{ from: "3 PM", subject: "Q2 model", time: "45m", priority: "high" }],
  },
  {
    id: "calendar",
    label: "Calendar",
    icon: "▣",
    color: C.GREEN,
    count: 3,
    urgent: 1,
    items: [{ from: "Today", subject: "Henderson", time: "5 PM", priority: "high" }],
  },
];

// Main-screen division id -> division-workspace id (/workspaces/:id).
//
// Security and Wellness were renamed to the display labels "Olivium" and
// "Vault", but their division ids are intentionally PRESERVED here to avoid
// routing/lookup risk (the DivisionIcon glyph switch and any id lookups keep
// working). Their CARDS route to the olivium / vault division workspaces. Every
// other division (including the Finance/Capital split from PR #90) maps to its
// own id. A division whose workspace does not exist yet (e.g. capital) resolves
// to the honest "unknown division" empty state in DivisionWorkspaceShell.
export const DIVISION_WORKSPACE_TARGET: Record<string, string> = {
  security: "olivium",
  wellness: "vault",
};

export function divisionWorkspaceTarget(id: string): string {
  return DIVISION_WORKSPACE_TARGET[id] ?? id;
}
